import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { CSSProperties } from 'react';
import { accountAmountRowPropertyNames } from '../components/enter-amounts-dialog/AccountAmountRowPropertyNames';
import { customPalette } from '../theme';
import { useEditableGridStyles } from './useEditableGridStyles';

export interface IEnterAmountsDialogStyles {
    modalStyles: string;
    dialogContentStyles: string;
    gridComponentCSSClass: string;
    totalRowStyle: CSSProperties;
    spinnerContainerClassName: string;
    dialogBodyStyles: string;
    comboboxStyles: string;
    headingStyles: string;
    subHeadingStyles: string;
    checkboxGroupStyles: string;
    enterAmountsDialogRootStyles: string;
    filterButtonWrapper: string;
    filterWrapper: string;
    gridWrapper: string;
    filterColumnStyles: string;
}

const useComboboxStyles = makeStyles({
    root: {
        minWidth: '300px',
        '&:after': {
            borderBottomColor: tokens.colorCompoundBrandStroke,
        },
        '>.fui-Option__checkIcon': {
            display: 'none',
        },
    },
});

const useDialogStyles = makeStyles({
    root: {
        padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
        maxWidth: '1315px',
        width: 'auto',
        maxHeight: '650px',
        height: 'auto',
        overflow: 'auto',
    },
});

const useDialogBodyStyles = makeStyles({
    root: {
        height: '100%',
    },
});

const useDialogContentStyles = makeStyles({ root: { overflowY: 'hidden', height: '100%' } });
const useDialogSubHeadingStyles = makeStyles({ root: { fontFamily: 'Segoe UI', fontSize: '14px', fontStyle: 'normal', fontWeight: 400 } });
const useCheckboxGroupStyles = makeStyles({ root: { display: 'flex', flexDirection: 'column' } });
const useFilterButtonWrapperStyles = makeStyles({ root: { display: 'flex', gap: '8px' } });
const useEnterAmountsDialogRootStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: '24px',
        height: 'inherit',
    },
});
const useFilterWrapperStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '8px',
        height: 'inherit',
    },
});
const useGridWrapperStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '12px',
        height: 'inherit',
    },
});
const useFilterColumnStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: '12',
        height: 'inherit',
    },
});

const useDialogHeadingStyles = makeStyles({
    root: {
        fontFamily: tokens.fontFamilyBase,
        fontSize: tokens.fontSizeBase500,
        fontWeight: tokens.fontWeightSemibold,
        lineheight: tokens.lineHeightBase500,
        margin: 0,
    },
});

const useSpinnerContainerStyles = makeStyles({
    root: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'content-box',
        height: '100%',
    },
});

const useAmountsGridStyles = makeStyles({
    root: {
        '&.e-grid': {
            display: 'flex',
            flexDirection: 'column',
            height: 'inherit',
            [`& .e-rowcell.${accountAmountRowPropertyNames.creditTotal}, & .e-rowcell.${accountAmountRowPropertyNames.debitTotal}, & .e-rowcell.${accountAmountRowPropertyNames.combinedTotal}, & .e-rowcell.${accountAmountRowPropertyNames.adjustedTotal}`]:
                {
                    borderLeft: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
                },
        },
        '& .e-gridcontent .e-content': {
            overflow: 'visible !important',
            '& .e-table .e-row, & .e-table .e-summaryrow': {
                [`& .e-rowcell.${accountAmountRowPropertyNames.accountCode}, & .e-rowcell.${accountAmountRowPropertyNames.accountDescription}, & .e-rowcell.${accountAmountRowPropertyNames.accountBalanceType}, & .e-rowcell.${accountAmountRowPropertyNames.adjustedTotal}`]:
                    {
                        backgroundColor: customPalette.statusCoolMidGrey,
                    },
                [`& .e-rowcell.${accountAmountRowPropertyNames.accountCode}`]: {
                    borderLeft: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
                },
            },
        },
    },
});

export const useEnterAmountsDialogStyles: () => IEnterAmountsDialogStyles = () => {
    const { gridComponentCSSClass, totalRowStyle } = useEditableGridStyles();
    const dialogContentStyles: string = useDialogContentStyles().root;
    const amountsGridStyles = useAmountsGridStyles();
    const spinnerContainerClassName = useSpinnerContainerStyles().root;

    return {
        modalStyles: useDialogStyles().root,
        dialogContentStyles,
        gridComponentCSSClass: mergeClasses(gridComponentCSSClass, amountsGridStyles.root),
        spinnerContainerClassName,
        totalRowStyle,
        dialogBodyStyles: useDialogBodyStyles().root,
        comboboxStyles: useComboboxStyles().root,
        headingStyles: useDialogHeadingStyles().root,
        subHeadingStyles: useDialogSubHeadingStyles().root,
        checkboxGroupStyles: useCheckboxGroupStyles().root,
        enterAmountsDialogRootStyles: useEnterAmountsDialogRootStyles().root,
        filterButtonWrapper: useFilterButtonWrapperStyles().root,
        filterWrapper: useFilterWrapperStyles().root,
        gridWrapper: useGridWrapperStyles().root,
        filterColumnStyles: useFilterColumnStyles().root,
    };
};
